import React, { useEffect, useState } from 'react';
import './SiteTopBar.css';
import { getPublicAssetUrl } from '../../utils/publicAssets';

const HELP_PHONE = '666 222 269';
const HELP_PHONE_HREF = 'tel:+34666222269';
const SCROLL_THRESHOLD = 24;
const MOBILE_MAX_WIDTH = 767;
const COMPACT_BAR_HEIGHT = '42px';
const EXPANDED_BAR_HEIGHT = '72px';

const SiteTopBar: React.FC = () => {
    const [isCompact, setIsCompact] = useState(false);
    const barRef = React.useRef<HTMLElement>(null);

    useEffect(() => {
        const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);

        const syncBarOffset = () => {
            const bar = barRef.current;
            if (!bar) {
                return;
            }
            // Sticky stepper must clear the real painted box, including border.
            const offset = Math.ceil(bar.getBoundingClientRect().height);
            document.documentElement.style.setProperty('--site-top-bar-offset', `${offset}px`);
        };

        const syncCompactState = () => {
            if (mobileQuery.matches) {
                setIsCompact(true);
                document.documentElement.style.setProperty('--site-top-bar-height', COMPACT_BAR_HEIGHT);
            } else {
                const scrolled = window.scrollY > SCROLL_THRESHOLD;
                setIsCompact(scrolled);
                document.documentElement.style.setProperty(
                    '--site-top-bar-height',
                    scrolled ? COMPACT_BAR_HEIGHT : EXPANDED_BAR_HEIGHT
                );
            }
            // Measure after height token updates paint.
            requestAnimationFrame(syncBarOffset);
        };

        const handleScroll = () => {
            if (!mobileQuery.matches) {
                syncCompactState();
            }
        };

        syncCompactState();
        mobileQuery.addEventListener('change', syncCompactState);
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', syncBarOffset);

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(syncBarOffset)
            : null;
        if (barRef.current && resizeObserver) {
            resizeObserver.observe(barRef.current);
        }

        return () => {
            mobileQuery.removeEventListener('change', syncCompactState);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', syncBarOffset);
            resizeObserver?.disconnect();
        };
    }, []);

    return (
        <header
            ref={barRef}
            className={`site-top-bar ${isCompact ? 'site-top-bar--compact' : ''}`}
            aria-label="Hanky Planet"
        >
            <div className="site-top-bar-inner">
                <a href="https://www.hankyplanet.com/" className="site-top-bar-logo-link">
                    <img
                        src={getPublicAssetUrl('img/logo.png')}
                        alt="Hanky Planet"
                        className="site-top-bar-logo"
                    />
                </a>
                <aside className="site-top-bar-help" aria-label="Ayuda con tu reserva">
                    <p className="site-top-bar-question">¿Tienes dudas?</p>
                    <a href={HELP_PHONE_HREF} className="site-top-bar-phone">
                        <span className="site-top-bar-phone-icon" aria-hidden="true">📞</span>
                        {HELP_PHONE}
                    </a>
                </aside>
            </div>
        </header>
    );
};

export default SiteTopBar;
